import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Input, Button, PasswordInput } from '@aqarmap/aqarmap-ui';
import useFormValidations from '~/hooks/useFormValidations';
import {
  useGetUserProfileQuery,
  useUpdateEmailMutation,
} from '~/redux/api/authApiSlice';
import ErrorMessage from '~/components/ErrorMessage';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import LayoutSkeleton from '~/components/LayoutSkeleton';
import { objectSerialize } from '~/lib';
import PageLayout from '~myAccount/components/PageLayout';
import { useFullRoutes } from '~/hooks';

const EmailUpdate = () => {
  const navigate = useNavigate();
  const { MY_ACCOUNT_URL } = useFullRoutes();
  const { data: userProfile, isLoading: userIsLoading } =
    useGetUserProfileQuery();
  const { t, i18n } = useTranslation('app');
  const locale = i18n.language;

  const [errorMessage, setErrorMessage] = useState('');
  const { emailSchema, passwordSchema } = useFormValidations();

  const updateEmailSchema = z.object({
    email: emailSchema,
    currentPassword: passwordSchema,
  });

  type IEmailUpdate = z.infer<typeof updateEmailSchema>;

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<IEmailUpdate>({
    resolver: zodResolver(updateEmailSchema),
    mode: 'onChange',
    values: { email: userProfile?.email as string, currentPassword: '' },
  });
  const [updateEmail, { isLoading }] = useUpdateEmailMutation();

  const onSubmit = async ({ email, currentPassword }: IEmailUpdate) => {
    const formData = objectSerialize({ email, currentPassword });
    const res: any = await updateEmail(formData);
    if (res.data && !res.error) {
      navigate(MY_ACCOUNT_URL.ROOT);
    }
    if (res.error) {
      res.error.data.errors.map((err: { field: string; message: any }) => {
        if (err.field === 'email' || err.field === 'currentPassword') {
          setError(err.field, { message: err.message });
        } else setErrorMessage(t('failedChangeEmail'));
      });
    }
  };

  return userIsLoading ? (
    <LayoutSkeleton />
  ) : (
    <PageLayout
      title={t('privacy')}
      bodyClassName="border border-gray rounded-[8px]"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        {errorMessage && <ErrorMessage message={errorMessage} />}
        <div className="flex flex-col gap-1.5x px-3x pt-3x">
          <div className="flex flex-col gap-3x ">
            <Controller
              control={control}
              name="email"
              render={({ field }) => (
                <Input
                  field={{
                    ...field,
                    placeholder: t('emailPlaceholder'),
                    autoFocus: true,
                    id: 'email',
                    type: 'email',
                  }}
                  error={errors[field.name]?.message}
                  label={{ text: t('email'), className: 'font-[600] block' }}
                />
              )}
            />
            <Controller
              control={control}
              name="currentPassword"
              render={({ field }) => (
                <PasswordInput
                  field={{
                    ...field,
                    placeholder: t('currentPassword'),
                    id: 'currentPassword',
                  }}
                  error={errors['currentPassword']?.message}
                  label={{
                    text: t('currentPassword'),
                    className: 'font-[600] block',
                  }}
                />
              )}
            />
          </div>
          <a
            href={`${process.env.REACT_APP_AUTH_BASE_URL}/${locale}/reset-password`}
            className="text-link-1 text-blue text-end"
          >
            {t('forgetPassword')}
          </a>
        </div>
        <div className="flex items-center gap-2x mt-3x p-3x border-t border-gray">
          <Button
            size="medium"
            ariaLabel="submit-btn"
            type="submit"
            variant="primary"
            isLoading={isLoading}
            disabled={isLoading}
            className="w-auto"
          >
            {t('save')}
          </Button>
          <Button
            size="medium"
            ariaLabel="cancel-btn"
            type="button"
            variant="secondary"
            className="w-auto"
            onClick={() => navigate(-1)}
          >
            {t('cancel')}
          </Button>
        </div>
      </form>
    </PageLayout>
  );
};

export default EmailUpdate;
